"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.otelGenaiProvider = void 0;
const __1 = require("../..");
const json_1 = require("../../../core/utils/json");
const tool_calls_1 = require("../../../core/normalize/message-parts/tool-calls");
/**
 * OTel GenAI event-stream convention (the semconv vocabulary also emitted by
 * Traceloop/OpenLLMetry and koog-style conversions): this module owns
 * choice-event envelopes, the snake_case `tool_call`/`tool_call_response`
 * types no SDK's own vocabulary claims, and the `gen_ai.tool.definitions`/
 * `llm.tools.N` attribute carriers.
 */
function normalizeOtelGenaiToolResult(value, context) {
    // GenAI carriers can retain AI SDK output envelopes. Delegate their contents
    // to the existing tool-result handler after translating the carrier fields.
    return (0, __1.claimed)(context.normalizePartValue({
        type: "tool-result",
        toolCallId: value.toolCallId /* ai sdk */ ??
            value.tool_use_id /* anthropic */ ??
            value.call_id /* openai responses */ ??
            value.id,
        toolName: value.toolName /* ai sdk */ ?? value.tool_name /* otel */,
        output: value.output /* ai sdk, openai responses */ ??
            value.response /* gemini */ ??
            value.result /* loose/traceloop */ ??
            value.content /* anthropic, openai chat message */,
        isError: [
            value.is_error /* anthropic */,
            value.isError /* ai sdk */,
        ].find((candidate) => typeof candidate === "boolean"),
    }));
}
const OTEL_GENAI_FINISH_REASON_TYPE_BY_RAW = {
    tool_call: "tool-calls",
};
const OTEL_GENAI_PART_HANDLERS = {
    tool_call: (value) => (0, __1.claimed)((0, tool_calls_1.toolCallPart)({
        toolCallId: value.toolCallId /* ai sdk */ ?? value.call_id ?? value.id,
        toolName: value.toolName /* ai sdk */ ?? value.name,
        input: value.input ??
            value.arguments /* otel */ ??
            value.args /* langchain */,
        toolType: (0, json_1.optionalString)(value.type),
    })),
    tool_call_response: normalizeOtelGenaiToolResult,
};
/** GenAI choice events: `{index?, message, finish_reason?}`. */
function unwrapOtelGenaiChoiceEvent(value, fallbackRole, ctx) {
    if (ctx.isMessageLike(value))
        return __1.unmatched;
    const nestedMessage = (0, json_1.asRecord)(value.message);
    if (!nestedMessage)
        return __1.unmatched;
    const message = ctx.normalizeMessage(nestedMessage, fallbackRole);
    if (!message)
        return __1.dropped;
    const finishReason = ctx.normalizeFinishReason(value) ?? message.finishReason;
    return (0, __1.claimed)((0, json_1.compact)({ ...message, finishReason }));
}
/**
 * Tool definitions among span attributes: the semconv
 * `gen_ai.tool.definitions` list, and Traceloop/OpenLLMetry's indexed
 * `llm.tools.N.tool.json_schema` keys (returned in index order).
 */
function otelGenaiToolDefinitionSources(carrier) {
    const attributes = carrier.metadataAttributes;
    if (!attributes)
        return [];
    const sources = [];
    if (attributes["gen_ai.tool.definitions"] !== undefined) {
        sources.push({
            sourceKey: "gen_ai.tool.definitions",
            value: attributes["gen_ai.tool.definitions"],
            options: { allowToolMap: true },
        });
    }
    const indexedToolKeys = Object.keys(attributes)
        .map((key) => ({
        key,
        index: /^llm\.tools\.(\d+)\.tool\.json_schema$/.exec(key)?.[1],
    }))
        .filter((entry) => entry.index !== undefined)
        .sort((left, right) => Number(left.index) - Number(right.index));
    for (const { key } of indexedToolKeys) {
        sources.push({ sourceKey: key, value: attributes[key] });
    }
    return sources;
}
exports.otelGenaiProvider = {
    name: "otel-genai",
    finishReasonTypeByRaw: OTEL_GENAI_FINISH_REASON_TYPE_BY_RAW,
    typedParts: OTEL_GENAI_PART_HANDLERS,
    tryUnwrapMessage: unwrapOtelGenaiChoiceEvent,
    // koog/Traceloop role-"tool" turns that are actually tool *definitions*
    // (an OpenAI function-tool wrapper with no tool_call_id) — not content.
    isToolDefinitionMessage: (message) => {
        const content = (0, json_1.asRecord)(message.content);
        return Boolean(message.role === "tool" &&
            content?.type === "function" &&
            content.function &&
            !message.tool_call_id);
    },
    collectToolDefinitionSources: otelGenaiToolDefinitionSources,
};
